'use client';

import React, { useState, useCallback, useMemo } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  CommandEmpty,
} from '@/components/ui/command';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SingleSelectOption<T = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface SingleSelectProps<T = string> {
  options: SingleSelectOption<T>[];
  value?: T;
  onChange: (value: T | undefined) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  renderItem?: (
    option: SingleSelectOption<T>,
    isSelected: boolean,
    onClick: () => void,
  ) => React.ReactNode;
  renderSelectedValue?: (option: SingleSelectOption<T>) => React.ReactNode;
  showClearButton?: boolean;
  disabled?: boolean;
  searchable?: boolean;
  maxHeight?: string;
  width?: string;
  error?: string | boolean;
  required?: boolean;
  onDropdownOpen?: () => void;
  headerContent?: React.ReactNode;
  portalContainer?: HTMLElement | null;
}

const SingleSelect = <T extends string = string>({
  options,
  value,
  onChange,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search options...',
  emptyText = 'No options found',
  className,
  renderItem,
  renderSelectedValue,
  showClearButton = true,
  disabled = false,
  searchable = true,
  maxHeight = 'tw-max-h-[350px]',
  width = 'tw-w-full',
  error,
  required = false,
  onDropdownOpen,
  headerContent,
  portalContainer,
}: SingleSelectProps<T>) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const isInvalid = Boolean(error) || (required && !value);
  const errorMessage = typeof error === 'string' ? error : '';
  const displayError =
    errorMessage || (isInvalid && required ? 'Required' : '');

  const filteredOptions = useMemo(() => {
    return options.filter(
      (option) =>
        !searchable ||
        option.label.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [options, searchValue, searchable]);

  const handleSelect = useCallback(
    (optionValue: T) => {
      onChange(optionValue);
      setOpen(false);
    },
    [onChange],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(undefined);
    },
    [onChange],
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      if (!newOpen) {
        setSearchValue('');
      } else if (onDropdownOpen) {
        onDropdownOpen();
      }
    },
    [onDropdownOpen],
  );

  const selectedOption = useMemo(() => {
    return value ? options.find((opt) => opt.value === value) : undefined;
  }, [value, options]);

  const DisplayContent = () => {
    if (!selectedOption) {
      return <span className="tw-text-muted-foreground">{placeholder}</span>;
    }
    if (renderSelectedValue) {
      return (
        <div className="tw-w-full">{renderSelectedValue(selectedOption)}</div>
      );
    }
    return (
      <span className="tw-block tw-truncate tw-w-full">
        {selectedOption.label}
      </span>
    );
  };

  const defaultRenderItem = useCallback(
    (
      option: SingleSelectOption<T>,
      isSelected: boolean,
      onClick: () => void,
    ) => (
      <CommandItem
        key={option.value}
        value={option.value}
        onSelect={onClick}
        disabled={option.disabled}
        className="tw-flex tw-items-center tw-justify-between tw-p-2 tw-cursor-pointer hover:tw-bg-gray-100 tw-h-9 tw-min-h-[2.25rem]"
      >
        <span className="tw-text-sm tw-truncate">{option.label}</span>
        <div
          className={cn(
            'tw-h-4 tw-w-4 tw-flex tw-items-center tw-justify-center tw-shrink-0',
            isSelected ? 'tw-text-primary' : 'tw-text-transparent',
          )}
        >
          <Check className="tw-h-3 tw-w-3" />
        </div>
      </CommandItem>
    ),
    [],
  );

  const itemRenderer = renderItem || defaultRenderItem;

  return (
    <div className="tw-relative">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'tw-justify-between tw-h-9 tw-text-sm tw-max-w-full tw-pr-2',
              width,
              isInvalid && '!tw-border-red-500 focus-visible:!tw-ring-red-500',
              disabled &&
                '!tw-bg-gray-50 tw-cursor-not-allowed !tw-pointer-events-auto',
              className,
            )}
            disabled={disabled}
          >
            <div className="tw-flex-1 tw-min-w-0 tw-text-left tw-overflow-hidden">
              <DisplayContent />
            </div>
            <div className="tw-flex tw-items-center tw-gap-1 tw-ml-2">
              {showClearButton && selectedOption && !disabled && (
                <button
                  onClick={handleClear}
                  className="tw-h-4 tw-w-4 tw-rounded-sm tw-opacity-50 hover:tw-opacity-100 tw-transition-opacity tw-flex tw-items-center tw-justify-center hover:tw-bg-gray-100"
                  type="button"
                >
                  <X className="tw-h-3 tw-w-3" />
                </button>
              )}
              <ChevronsUpDown className="tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(width, '!tw-p-0')}
          align="start"
          style={{ width: 'var(--radix-popover-trigger-width)' }}
          container={portalContainer}
        >
          <Command shouldFilter={false}>
            {headerContent && (
              <div className="tw-px-3 tw-pt-3 tw-pb-2 tw-border-b tw-border-gray-200">
                {headerContent}
              </div>
            )}
            {searchable && (
              <CommandInput
                placeholder={searchPlaceholder}
                value={searchValue}
                onValueChange={setSearchValue}
                className="tw-border-none focus:tw-ring-0 focus:tw-border-none focus:tw-outline-none !tw-border-b-0"
              />
            )}
            <CommandList className={maxHeight}>
              {filteredOptions.length === 0 && (
                <CommandEmpty>{emptyText}</CommandEmpty>
              )}
              {filteredOptions.map((option) => {
                const isSelected = value === option.value;
                return itemRenderer(option, isSelected, () =>
                  handleSelect(option.value),
                );
              })}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {isInvalid && displayError && (
        <span className="tw-absolute tw-top-full tw-left-2 tw-text-xs tw-text-red-500 tw--mt-2 tw-px-1 tw-rounded-sm tw-bg-white">
          {displayError}
        </span>
      )}
    </div>
  );
};

export default SingleSelect;
